import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Select, message } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import DataKitApi from "../../../../api/DataKitApi";
import { DataKitMenuDto, MenuDesignDto } from "../../../../domain/DataKitDto";
import { env } from "../../../../env/env";
import "./DataKitDrawer.scss";
import "./DataKitMenuDrawer.scss";
import { useDrawerDismiss } from "./useDrawerDismiss";

const thumbnailUrl = (designId: string) =>
  `${env.rendererPath}/design/thumbnail/${designId}?height=80`;

interface Props {
  menu: DataKitMenuDto;
  canWrite: boolean;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Edit one account menu: its name, its note, and which designs feed it.
 *
 * The name is the public one — website tabs, listings entries, page titles — while a design's own
 * file name is internal metadata. That is why this drawer shows the menu name as the heading and
 * design names only ever as rows in a list: "DIN-DAL-2026-v6_FINAL(2)" is never presented as a
 * title.
 */
export function DataKitMenuDrawer({ menu, canWrite, onClose, onSaved }: Props) {
  const menuId = menu.id as string;

  const [name, setName] = useState(menu.name);
  const [note, setNote] = useState(menu.note || "");
  const [designs, setDesigns] = useState<MenuDesignDto[]>([]);
  const [candidates, setCandidates] = useState<MenuDesignDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachId, setAttachId] = useState<string | undefined>();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([DataKitApi.listMenuDesigns(menuId), DataKitApi.listAttachableDesigns()])
      .then(([onMenu, all]) => {
        setDesigns(onMenu.data || []);
        setCandidates(all.data || []);
      })
      .catch(() => message.error("Could not load this menu's designs"))
      .finally(() => setLoading(false));
  }, [menuId]);

  useEffect(() => {
    load();
  }, [load]);

  const { closing, dismiss } = useDrawerDismiss();
  const close = useCallback(() => dismiss(onClose), [dismiss, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !attachOpen && !confirmDelete) close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [close, attachOpen, confirmDelete]);

  const attachable = useMemo(
    () => candidates.filter((d) => d.menuId !== menuId),
    [candidates, menuId]
  );

  const attachTarget = useMemo(
    () => attachable.find((d) => d.designId === attachId),
    [attachable, attachId]
  );

  const saveDetails = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    DataKitApi.updateMenu(menuId, { name: trimmed, note: note.trim() || null })
      .then(() => dismiss(onSaved))
      .catch(() => message.error("Could not save this menu"))
      .finally(() => setSaving(false));
  };

  const detach = (designId: string) => {
    setSaving(true);
    DataKitApi.setDesignMenu(designId, { menuId: null })
      .then(() => {
        setDesigns((current) => current.filter((d) => d.designId !== designId));
        load();
      })
      .catch(() => message.error("Could not detach that design"))
      .finally(() => setSaving(false));
  };

  const attach = () => {
    if (!attachId) return;
    setSaving(true);
    DataKitApi.setDesignMenu(attachId, { menuId })
      .then(() => {
        setAttachOpen(false);
        setAttachId(undefined);
        load();
      })
      .catch(() => message.error("Could not attach that design"))
      .finally(() => setSaving(false));
  };

  const remove = () => {
    setDeleting(true);
    DataKitApi.deleteMenu(menuId)
      .then(() => {
        setConfirmDelete(false);
        dismiss(onSaved);
      })
      .catch(() =>
        message.error("Could not delete this menu - detach its designs and clear its prices first")
      )
      .finally(() => setDeleting(false));
  };

  return (
    <>
      <div className={`data-kit-drawer-scrim${closing ? " closing" : ""}`} onClick={close} />
      <aside
        className={`data-kit-menu-drawer${closing ? " closing" : ""}`}
        role="dialog"
        aria-label={`Edit ${menu.name}`}
      >
        <header className="data-kit-menu-drawer-header">
          <h2>{name || "Menu"}</h2>
          <button type="button" className="data-kit-drawer-close" onClick={close} aria-label="Close">
            <CloseOutlined />
          </button>
        </header>

        <div className="data-kit-menu-drawer-body">
          <label className="data-kit-field">
            <span>Name</span>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!canWrite}
              maxLength={255}
            />
            <span className="data-kit-field-hint">
              Guests see this name — on website tabs, listings and page titles.
            </span>
          </label>

          <label className="data-kit-field">
            <span>Note</span>
            <Input
              placeholder="e.g. 3-6pm - bar and patio only"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={!canWrite}
              maxLength={500}
            />
          </label>

          <div className="data-kit-field">
            <div className="menu-designs-header">
              <span>Designs on this menu</span>
              {canWrite && (
                <Button
                  size="small"
                  onClick={() => {
                    setAttachId(undefined);
                    setAttachOpen(true);
                  }}
                >
                  + Attach design
                </Button>
              )}
            </div>

            {designs.length === 0 && !loading && (
              <p className="menu-designs-empty">
                Nothing yet. Attach a design and its prices follow this menu.
              </p>
            )}

            <div className="menu-designs-list">
              {designs.map((design) => (
                <div className="menu-design-row" key={design.designId}>
                  <img
                    className="menu-design-thumb"
                    src={thumbnailUrl(design.designId)}
                    alt=""
                    loading="lazy"
                  />
                  <span className="menu-design-name">
                    {design.designName || "Untitled design"}
                  </span>
                  {canWrite && (
                    <button
                      type="button"
                      className="menu-design-detach"
                      onClick={() => detach(design.designId)}
                      disabled={saving}
                    >
                      Detach
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {canWrite && (
            <button
              type="button"
              className="menu-delete-link"
              onClick={() => setConfirmDelete(true)}
            >
              Delete this menu
            </button>
          )}
        </div>

        <footer className="data-kit-drawer-footer">
          {/* No Cancel: the header X closes, exactly as Cancel did (Lyra, Oct 1). */}
          <div className="data-kit-drawer-footer-buttons">
            <Button
              type="primary"
              onClick={saveDetails}
              loading={saving}
              disabled={!canWrite || !name.trim()}
            >
              Save
            </Button>
          </div>
        </footer>
      </aside>

      <Modal
        className={"data-kit-attach-modal clean one"}
        open={attachOpen}
        width={560}
        onCancel={() => setAttachOpen(false)}
      >
        <header>
          <h2>Attach a design</h2>
        </header>
        <Select
          className="menu-attach-select"
          size="large"
          showSearch
          placeholder="Choose a design"
          value={attachId}
          onChange={setAttachId}
          optionFilterProp="label"
          options={attachable.map((d) => ({
            label: d.designName || "Untitled design",
            value: d.designId,
          }))}
        />

        {attachTarget && (
          <div className="menu-attach-preview">
            <img src={thumbnailUrl(attachTarget.designId)} alt="" />
            <div className="menu-attach-preview-text">
              <div className="menu-attach-preview-name">
                {attachTarget.designName || "Untitled design"}
              </div>
              {attachTarget.menuName && (
                <div className="menu-attach-preview-warning">
                  Currently on {attachTarget.menuName} — attaching moves it here.
                </div>
              )}
            </div>
          </div>
        )}

        <footer>
          <Button size="large" onClick={() => setAttachOpen(false)}>
            Cancel
          </Button>
          <Button
            size="large"
            className="primary"
            loading={saving}
            disabled={!attachId}
            onClick={attach}
          >
            Attach
          </Button>
        </footer>
      </Modal>

      <Modal
        className={"clean one"}
        open={confirmDelete}
        onCancel={() => setConfirmDelete(false)}
      >
        <header>
          <h2>Delete {menu.name}?</h2>
        </header>
        <p className="data-kit-discard-copy">
          Only possible once no designs point at it and no item prices use it. Anything still
          attached has to be moved first.
        </p>
        <footer>
          <Button size="large" onClick={() => setConfirmDelete(false)}>
            Cancel
          </Button>
          <Button size="large" className="primary" loading={deleting} onClick={remove}>
            Delete
          </Button>
        </footer>
      </Modal>
    </>
  );
}
